import { useLocation, Link } from 'react-router-dom';
import { DocsLayout } from '../components/docs/DocsLayout';
import { DocMarkdown } from '../components/docs/DocMarkdown';
import { DocsTOC } from '../components/docs/DocsTOC';
import { findDocByPath, getDocIndex } from '../lib/docs';
import { FeatureStatus } from '../components/docs/FeatureStatus';
import { Icon, type IconName } from '../components/Icon';
import { useEffect } from 'react';

/**
 * Rendu d'une page de documentation en fonction du chemin courant.
 * Route : /docs, /docs/:section, /docs/:section/:slug (+ slugs imbriqués)
 */
export function DocsPage() {
    const location = useLocation();
    const page = findDocByPath(location.pathname);
    const { sections } = getDocIndex();

    // Scroll en haut à chaque changement de page
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [location.pathname]);

    // Page d'accueil /docs
    if (location.pathname === '/docs' || location.pathname === '/docs/') {
        return (
            <DocsLayout>
                <DocsHome />
            </DocsLayout>
        );
    }

    if (!page) {
        return (
            <DocsLayout>
                <div className="docs-article">
                    <div className="docs-404">
                        <span className="dash-eyebrow">404</span>
                        <h1>Page de documentation introuvable</h1>
                        <p>
                            Le lien que vous avez suivi n'existe pas ou a été déplacé.
                            Revenez à l'accueil pour parcourir la documentation.
                        </p>
                        <Link to="/docs" className="btn-primary">
                            <Icon name="home" size={14} />
                            Retour à l'accueil
                        </Link>
                    </div>
                </div>
            </DocsLayout>
        );
    }

    const section = sections.find((s) => s.id === page.section);

    return (
        <DocsLayout>
            <article className="docs-article">
                <div className="docs-article-layout">
                    <div className="docs-article-main">
                        <nav className="docs-breadcrumb" aria-label="Fil d'ariane">
                            <Link to="/docs">Docs</Link>
                            <span aria-hidden>/</span>
                            {section && (
                                <>
                                    <Link to={section.indexPath}>{section.label}</Link>
                                    <span aria-hidden>/</span>
                                </>
                            )}
                            <span className="docs-breadcrumb-current">
                                {page.frontmatter.title}
                            </span>
                        </nav>

                        <header className="docs-article-head">
                            <h1>{page.frontmatter.title}</h1>
                            {page.frontmatter.description && (
                                <p className="docs-lede">{page.frontmatter.description}</p>
                            )}
                            <div className="docs-article-meta">
                                {page.frontmatter.status && (
                                    <FeatureStatus status={page.frontmatter.status} />
                                )}
                                {page.frontmatter.updated && (
                                    <span className="docs-updated">
                                        Mis à jour le {page.frontmatter.updated}
                                    </span>
                                )}
                            </div>
                        </header>

                        <DocMarkdown content={page.content} />

                        <DocsPagerFooter sectionId={page.section} currentPath={page.path} />
                    </div>

                    <div className="docs-article-side">
                        <DocsTOC content={page.content} />
                    </div>
                </div>
            </article>
        </DocsLayout>
    );
}

/* ================================================================
   Pager bas de page : précédent / suivant dans la section
   ================================================================ */
function DocsPagerFooter({
    sectionId,
    currentPath,
}: {
    sectionId: string;
    currentPath: string;
}) {
    const { sections } = getDocIndex();
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return null;
    const idx = section.pages.findIndex((p) => p.path === currentPath);
    const prev = idx > 0 ? section.pages[idx - 1] : null;
    const next = idx >= 0 && idx < section.pages.length - 1 ? section.pages[idx + 1] : null;
    if (!prev && !next) return null;
    return (
        <div className="docs-pager">
            {prev ? (
                <Link to={prev.path} className="docs-pager-card is-prev">
                    <span className="docs-pager-label">Précédent</span>
                    <strong>{prev.frontmatter.title}</strong>
                </Link>
            ) : (
                <span />
            )}
            {next ? (
                <Link to={next.path} className="docs-pager-card is-next">
                    <span className="docs-pager-label">Suivant</span>
                    <strong>{next.frontmatter.title}</strong>
                </Link>
            ) : (
                <span />
            )}
        </div>
    );
}

/* ================================================================
   Page d'accueil de la doc : hero + cartes par section
   ================================================================ */
function DocsHome() {
    const { sections } = getDocIndex();
    return (
        <div className="docs-article docs-home">
            <header className="docs-home-hero">
                <span className="dash-eyebrow">Documentation Quotio</span>
                <h1>Tout ce qu'il faut pour concevoir, publier et intégrer votre générateur de devis.</h1>
                <p>
                    Des guides pas-à-pas pour démarrer en 5 minutes, la référence
                    complète de l'éditeur, et le nécessaire technique pour embarquer
                    Quotio sur votre site.
                </p>
                <div className="docs-home-ctas">
                    <Link to="/docs/getting-started/quickstart" className="btn-primary">
                        <Icon name="lightning" size={15} />
                        Quickstart 5 min
                    </Link>
                    <Link to="/docs/embed/choisir-son-mode" className="btn-ghost">
                        <Icon name="code" size={15} />
                        Intégrer sur mon site
                    </Link>
                </div>
            </header>

            <section className="docs-home-grid">
                {sections.map((section) => (
                    <Link
                        key={section.id}
                        to={section.indexPath}
                        className="docs-home-card"
                    >
                        <span className="docs-home-card-icon">
                            <Icon name={(section.icon ?? 'book') as IconName} size={18} />
                        </span>
                        <strong>{section.label}</strong>
                        {section.description && <p>{section.description}</p>}
                        <span className="docs-home-card-count">
                            {section.pages.length} page{section.pages.length > 1 ? 's' : ''}
                        </span>
                    </Link>
                ))}
            </section>
        </div>
    );
}
